import { z } from "../zod.js";
import {
  DemandRefreshStats,
  DemandRefreshTrigger,
  MpsDeltaPct,
  PlanningDate,
  PlanningMonday,
  PlanningQty,
} from "../planning/schemas.js";
import {
  MrpRunTrigger,
  PlannedOrderConvertedKind,
  PlannedOrderKind,
} from "../planning/mrp-schemas.js";
import { ScheduleKpis, ScheduleTrigger } from "../planning/schedule-schemas.js";
import {
  PromiseFulfillmentPath,
  PromiseRequestOutcome,
  PromiseRiskReason,
  PromiseSource,
  PromiseWithdrawnReasonKey,
} from "../planning/promise-schemas.js";

// Module 03 planning domain events (spec 03 §6): what the module emits and
// what it consumes over the relay. Seventeen producer names: G-2 registered
// `planning.demand_refreshed`, G-3 `planning.mps_proposed` and
// `planning.mps_approved`, P3-H-2 `planning.mrp_completed`,
// `planning.transfer_demand_projected` and `planning.planned_order_firmed`,
// P3-H-3 `planning.planned_order_converted` (6 → 7, D-389 ruling 3),
// P3-I-2 `planning.schedule_published` and `planning.capacity_overloaded`
// (7 → 9, D-399 ruling 6, D-401 ruling 3), P3-J J-1
// `planning.promise_committed` and `planning.promise_withdrawn` (9 → 11,
// D-468 ruling 1), P3-J J-2 `planning.order_promises_settled` (11 → 12,
// D-465 ruling 2), P3-J J-4 `planning.scenario_promoted` (12 → 13,
// D-472 ruling 3), and P3-J J-3's four watch events
// `planning.promise_at_risk`, `planning.promise_broken`,
// `planning.promise_recovered` and `planning.promise_kept` (13 → 17, D-468
// ruling 1), each with its emitter. The count here moves with
// `apps/api/src/modules/planning/planning-events.ts`.

/** Spec 03 §3.1 step 6: a refresh finished and its pool is the facility's pool of record. */
export const PlanningDemandRefreshedPayload = z.object({
  demand_refresh_id: z.guid(),
  facility_id: z.guid(),
  trigger: DemandRefreshTrigger,
  stats: DemandRefreshStats,
  mps_relevant_changed: z.boolean(),
});

/** Spec 03 §3.2 step 4: a plan was proposed at the facility, superseding the open plan if any. */
export const PlanningMpsProposedPayload = z.object({
  mps_plan_id: z.guid(),
  plan_no: z.string(),
  facility_id: z.guid(),
  horizon_start: PlanningMonday,
  horizon_weeks: z.number().int().min(1).max(52),
  /** The `proposed` or `adjusting` plan this one replaced, null when none was open. */
  superseded_plan_id: z.guid().nullable(),
});

/**
 * Spec 03 §3.2: a plan became the facility's approved plan, by an approver or
 * by its own submit. It carries no quantity total, because a plan's products
 * are counted in different base units (D-350 ruling 8).
 */
export const PlanningMpsApprovedPayload = z.object({
  mps_plan_id: z.guid(),
  plan_no: z.string(),
  facility_id: z.guid(),
  approved_by: z.guid(),
  auto_approved: z.boolean(),
  delta_pct: MpsDeltaPct,
});

/** Spec 03 §3.3 step 15: a run succeeded and wrote its orders. A failure is the run row, never an event. */
export const PlanningMrpCompletedPayload = z.object({
  mrp_run_id: z.guid(),
  run_no: z.string(),
  facility_id: z.guid(),
  trigger: MrpRunTrigger,
  mps_plan_id: z.guid().nullable(),
  stats: z.object({
    planned_orders_created: z.number().int().min(0),
    planned_orders_updated: z.number().int().min(0),
    planned_orders_cancelled: z.number().int().min(0),
    action_messages: z.number().int().min(0),
  }),
});

/**
 * Spec 03 §3.3's propagation (D-388 ruling 7): one event per source facility
 * whose transfer orders a run created, requantified, redated or cancelled.
 * `facility_id` is the run's facility, the destination. Each handler re-reads
 * state and trusts nothing in the payload but ids and the depth.
 */
export const PlanningTransferDemandProjectedPayload = z.object({
  mrp_run_id: z.guid(),
  facility_id: z.guid(),
  orchestration_id: z.guid().nullable(),
  propagation: z.object({ origin_run_id: z.guid(), depth: z.number().int().min(0) }),
  source_facility_id: z.guid(),
  destination_facility_id: z.guid(),
  products: z.array(
    z.object({
      product_id: z.guid(),
      transfer_planned_order_ids: z.array(z.guid()),
      /** Σ quantity of the proposed and firmed orders, in the product's base unit. */
      total_qty: PlanningQty,
      earliest_due_date: PlanningDate.nullable(),
    }),
  ),
});

/** Spec 03 §6: a planned order was firmed, by a person or the agent. */
export const PlanningPlannedOrderFirmedPayload = z.object({
  planned_order_id: z.guid(),
  po_no: z.string(),
  facility_id: z.guid(),
  kind: PlannedOrderKind,
  product_id: z.guid(),
  qty: PlanningQty,
  due_date: PlanningDate,
});

/** Spec 03 §6: a firmed planned order became its document (D-389 ruling 3). */
export const PlanningPlannedOrderConvertedPayload = z.object({
  planned_order_id: z.guid(),
  po_no: z.string(),
  facility_id: z.guid(),
  converted_kind: PlannedOrderConvertedKind,
  converted_ref_id: z.guid(),
});

/**
 * Spec 03 §6 (D-399 ruling 6): a schedule became the facility's published
 * schedule, by a person or by the system. It carries the KPIs and the counts
 * and no money.
 */
export const PlanningSchedulePublishedPayload = z.object({
  schedule_id: z.guid(),
  facility_id: z.guid(),
  version: z.number().int().min(1),
  trigger: ScheduleTrigger,
  auto: z.boolean(),
  /** Null when the system published it under §3.5's auto-publish tests. */
  published_by: z.guid().nullable(),
  kpis: ScheduleKpis,
  moved_ops_count: z.number().int().min(0),
  overloads_count: z.number().int().min(0),
});

/**
 * Spec 03 §6 (D-401 ruling 3): at a publish, one event per overload new
 * against the superseded schedule. It carries no moves and no money; a
 * consumer reads the row through §5 row 66, whose narrowing applies to it.
 */
export const PlanningCapacityOverloadedPayload = z.object({
  schedule_overload_id: z.guid(),
  schedule_id: z.guid(),
  facility_id: z.guid(),
  work_center_id: z.guid(),
  week_start: PlanningMonday,
  overload_minutes: z.number().int().min(1),
  late_op_count: z.number().int().min(0),
});

/**
 * Spec 03 §6 (D-468 ruling 1): a promise was committed to a sales order
 * line, by a person, the agent, the confirmation drain or a re-promise. It
 * carries no money and no quantity; a consumer reads the promise.
 */
export const PlanningPromiseCommittedPayload = z.object({
  promise_id: z.guid(),
  promise_no: z.string(),
  facility_id: z.guid(),
  sales_order_id: z.guid(),
  sales_order_line_id: z.guid(),
  promised_date: PlanningDate,
  fulfillment_path: PromiseFulfillmentPath,
  source: PromiseSource,
  /** The committed promise a re-promise replaced (J-3); null for every other commit. */
  replaces_promise_id: z.guid().nullable(),
});

/**
 * Spec 03 §6 (D-468 ruling 1): a promise was withdrawn, by a person, a
 * re-promise, an order cancel or a line cut to nothing (D-466 ruling 6).
 * `was_committed` says whether a customer's date stood behind it.
 */
export const PlanningPromiseWithdrawnPayload = z.object({
  promise_id: z.guid(),
  facility_id: z.guid(),
  sales_order_line_id: z.guid().nullable(),
  reason_key: PromiseWithdrawnReasonKey,
  was_committed: z.boolean(),
});

/**
 * Spec 03 §3.7a (D-465 ruling 2): the confirmation drain ended an order's
 * last request. Each line names its outcome, and its promise and date when
 * it has one; module 09 enqueues the Confirmed message from it. No money,
 * no quantity.
 */
export const PlanningOrderPromisesSettledPayload = z.object({
  sales_order_id: z.guid(),
  lines: z
    .array(
      z.object({
        sales_order_line_id: z.guid(),
        promise_id: z.guid().nullable(),
        promised_date: PlanningDate.nullable(),
        outcome: PromiseRequestOutcome,
      }),
    )
    .min(1),
});

/**
 * Spec 03 §3.11 (D-466 ruling 3): the watch found a committed promise at
 * risk. `order_value_minor` and `currency` are the line's value from module
 * 09's line reader (D-464 ruling 5), for module 12's exception in P4; no
 * person's screen reads them. `released_by` is the actor of the release that
 * took a cited hold, null when the reason is not `stock_consumed` or no
 * person released it.
 */
export const PlanningPromiseAtRiskPayload = z.object({
  promise_id: z.guid(),
  promise_no: z.string(),
  facility_id: z.guid(),
  sales_order_id: z.guid(),
  sales_order_line_id: z.guid(),
  risk_reason: PromiseRiskReason,
  promised_date: PlanningDate,
  /** Null when the published schedule left the line's supply unplaced. */
  current_projected_date: PlanningDate.nullable(),
  order_value_minor: z.number().int().min(0),
  currency: z.string().regex(/^[A-Z]{3}$/),
  released_by: z.guid().nullable(),
});

/**
 * Spec 03 §3.11 (D-467 ruling 2): the promised date passed and the shipments
 * dated on or before it do not cover the judged quantity. The verdict's
 * figures ride the event; `risk_reason` is the reason the watch last named,
 * null when the promise broke without being flagged first.
 */
export const PlanningPromiseBrokenPayload = z.object({
  promise_id: z.guid(),
  promise_no: z.string(),
  facility_id: z.guid(),
  sales_order_id: z.guid(),
  sales_order_line_id: z.guid(),
  risk_reason: PromiseRiskReason.nullable(),
  promised_date: PlanningDate,
  current_projected_date: PlanningDate.nullable(),
  order_value_minor: z.number().int().min(0),
  currency: z.string().regex(/^[A-Z]{3}$/),
  released_by: z.guid().nullable(),
  slip_days: z.number().int().min(0),
  shortfall_qty: PlanningQty,
  pct_shipped_on_time: z.string(),
  downgraded: z.boolean(),
});

/** Spec 03 §3.11 (D-466 ruling 4): an at-risk promise projects on time again with its basis holding. */
export const PlanningPromiseRecoveredPayload = z.object({
  promise_id: z.guid(),
  promise_no: z.string(),
  facility_id: z.guid(),
  sales_order_id: z.guid(),
  sales_order_line_id: z.guid(),
  promised_date: PlanningDate,
  current_projected_date: PlanningDate,
});

/**
 * Spec 03 §3.11 (D-467 rulings 2 and 3): the shipments dated on or before the
 * promised date cover it. `corrected` is true for the one move from `broken`
 * to `kept` a late-recorded on-time shipment makes.
 */
export const PlanningPromiseKeptPayload = z.object({
  promise_id: z.guid(),
  promise_no: z.string(),
  facility_id: z.guid(),
  sales_order_id: z.guid(),
  sales_order_line_id: z.guid(),
  promised_date: PlanningDate,
  shipped_date: PlanningDate.nullable(),
  corrected: z.boolean(),
});

/**
 * Producer names with their consumers. A module joins a row's `consumers` in
 * the commit that registers its subscriber, so the MPS proposer named `"03"`
 * on `planning.demand_refreshed` in G-3 (spec 03 §6).
 */
/**
 * Spec 03 §3.9 as built: a person promoted a solved scenario, writing its
 * persistable hypotheses to the live calendar (D-472 ruling 3). Ids only.
 */
export const PlanningScenarioPromotedPayload = z.object({
  scenario_id: z.guid(),
  facility_id: z.guid(),
  applied_changes: z.object({
    exception_ids: z.array(z.guid()),
    pattern_ids: z.array(z.guid()),
    assignment_ids: z.array(z.guid()),
  }),
});

export const planningProducerEvents = {
  // Module 03's MPS proposer (G-3); module 14 is declared absent until P5.
  "planning.demand_refreshed": {
    payload: PlanningDemandRefreshedPayload,
    consumers: ["03"],
  },
  // Modules 13 and 14 are declared absent until P5.
  "planning.mps_proposed": {
    payload: PlanningMpsProposedPayload,
    consumers: [],
  },
  // Module 03's MRP enqueue (P3-H-2, D-388 ruling 6); 12 lands in P4, 14 in P5.
  "planning.mps_approved": {
    payload: PlanningMpsApprovedPayload,
    consumers: ["03"],
  },
  // The scheduler arms its solve for the rough-cut load (P3-I-2, D-401
  // ruling 4); 10 is P4's, 13 and 14 P5's (D-388 ruling 7).
  "planning.mrp_completed": {
    payload: PlanningMrpCompletedPayload,
    consumers: ["03"],
  },
  // Module 03 queues a run at the source facility (P3-H-2, D-388 ruling 6);
  // 14 is declared absent until P5.
  "planning.transfer_demand_projected": {
    payload: PlanningTransferDemandProjectedPayload,
    consumers: ["03"],
  },
  // The scheduler arms its solve (P3-I-2); 05-C's match scan (P4-0, D-369)
  // and module 10 register with their slices (spec 03 §6).
  "planning.planned_order_firmed": {
    payload: PlanningPlannedOrderFirmedPayload,
    consumers: ["03"],
  },
  // P3-H-3 (D-389 ruling 3). Modules 04 and 06 are not consumers:
  // conversion calls them synchronously, and a consumer there would act a
  // second time (D-389's rejected alternative). The scheduler arms its solve
  // (P3-I-2, spec 03 §6); module 10 registers in P4.
  "planning.planned_order_converted": {
    payload: PlanningPlannedOrderConvertedPayload,
    consumers: ["03"],
  },
  // P3-I-2 (D-399 ruling 6). Module 04 is struck, since the publish writes
  // its columns synchronously. P3-J J-3's promise watch arms its debounce on
  // it (D-466 ruling 1); 12, 13 and 14 register with their slices.
  "planning.schedule_published": {
    payload: PlanningSchedulePublishedPayload,
    consumers: ["03"],
  },
  // P3-I-2 (D-401 ruling 3). No consumer in P3-I: module 12's exception and
  // module 13 register with their slices (P4).
  "planning.capacity_overloaded": {
    payload: PlanningCapacityOverloadedPayload,
    consumers: [],
  },
  // P3-J J-1 (D-468 ruling 1). Module 09 stamps the line and emits its
  // `promise_dated` line change from J-2 (D-465 ruling 4). Module 06 is
  // struck, since the commit reserves synchronously (D-468 ruling 4); module
  // 13 is declared absent until P5.
  "planning.promise_committed": {
    payload: PlanningPromiseCommittedPayload,
    consumers: ["09"],
  },
  // P3-J J-1 (D-468 ruling 1). Module 09 clears its mirror from J-2.
  "planning.promise_withdrawn": {
    payload: PlanningPromiseWithdrawnPayload,
    consumers: ["09"],
  },
  // P3-J J-2 (D-465 ruling 2). Module 09 enqueues the Confirmed message.
  "planning.order_promises_settled": {
    payload: PlanningOrderPromisesSettledPayload,
    consumers: ["09"],
  },
  // J-4: no consumer; modules 13 and 12 are declared absent (P5, P4).
  "planning.scenario_promoted": {
    payload: PlanningScenarioPromotedPayload,
    consumers: [],
  },
  // P3-J J-3 (D-466, D-467, D-468 ruling 2): the watch's four transitions.
  // Module 09 keeps the order line's mirror from each (spec 09 §4.7); module
  // 12's exception lands in P4 and module 13's analytics in P5, both declared
  // absent. None messages a customer (D-469 ruling 3).
  "planning.promise_at_risk": {
    payload: PlanningPromiseAtRiskPayload,
    consumers: ["09"],
  },
  "planning.promise_broken": {
    payload: PlanningPromiseBrokenPayload,
    consumers: ["09"],
  },
  "planning.promise_recovered": {
    payload: PlanningPromiseRecoveredPayload,
    consumers: ["09"],
  },
  "planning.promise_kept": {
    payload: PlanningPromiseKeptPayload,
    consumers: ["09"],
  },
} as const;

export type PlanningEventName = keyof typeof planningProducerEvents;

export const planningProducerEventNames = Object.keys(
  planningProducerEvents,
) as PlanningEventName[];

/**
 * The events module 03 subscribes to, exactly: the six of G-2, each of which
 * only arms the facility's demand debounce (spec 03 §3.1, D-349 ruling 14),
 * G-3's MPS proposer on `planning.demand_refreshed` (§3.2), P3-H-2's MRP
 * enqueue on `planning.mps_approved` and the projection consumer on
 * `planning.transfer_demand_projected` (§3.3, D-388 ruling 6), and P3-I-2's
 * ten scheduler triggers, each arming the solve debounce (§6, D-398 ruling
 * 2). Nineteen. P3-J J-3 adds the promise watch's two triggers,
 * `planning.schedule_published` and `inventory.reservation_released`, each
 * arming the watch's debounce (D-466 ruling 1, D-468 ruling 3). Twenty-one.
 * `onboarding.went_live` is not consumed, because the planning worker runs
 * whatever the tenant's live status.
 */
export const planningConsumedEventNames = [
  "sales.order_confirmed",
  "sales.order_cancelled",
  "sales.order_line_changed",
  "sales.order_credit_released",
  "sales.shipment_shipped",
  "onboarding.import_committed",
  "planning.demand_refreshed",
  "planning.mps_approved",
  "planning.transfer_demand_projected",
  "planning.mrp_completed",
  "planning.planned_order_firmed",
  "planning.planned_order_converted",
  "production.work_order_released",
  "production.operation_started",
  "production.operation_completed",
  "production.work_order_completed",
  "production.work_order_cancelled",
  "production.work_order_reprioritized",
  "production.operation_hold_changed",
  "planning.schedule_published",
  "inventory.reservation_released",
] as const;
